// The summary row: a native table footer whose values aggregate the complete matching set, not the visible
// page. Each value sits under its owning column, in that column's display formatting and locked-edge style;
// the consumer's label is the row header. It renders only while rows are shown, so the zero-data,
// zero-results, loading and error states reserve no space for it.

import PropTypes from 'prop-types'
import React from 'react'

import { useTable } from '../context'
import { formatDate, formatNumber, isNumber, plainValue } from '../format'
import { cellClass } from './registry'
import { ClippedValue } from '../tooltip/Tooltip'

// in Compact density a total stays on one line and discloses its full value while clipped
const SummaryValue = ({ text, clip }) =>
  clip && text ? <ClippedValue text={text} /> : text

SummaryValue.propTypes = {
  text: PropTypes.string,
  clip: PropTypes.bool,
}

const has = (totals, key) => Object.prototype.hasOwnProperty.call(totals, key)
const NO_EDGES = { start: false, end: false }

// A total is a plain value; a column's own value() or render() reads rows, so neither applies here.
export const formatTotal = (column, total) => {
  if (total === null || total === undefined) return ''
  if (column.type === 'numeric')
    return isNumber(total) ? formatNumber(total, column.format) : String(total)
  if (column.type === 'date')
    return formatDate(total, undefined, column.dateStyle) || String(total)
  const plain = plainValue(total)
  return plain === null || plain === undefined ? '' : String(plain)
}

export const SummaryRow = () => {
  const { config, derived, layout, geometry, state } = useTable()
  if (!layout.showSummary || !config.summary) return null
  const compact = state?.view?.density === 'compact'
  const totals = derived.totals || {}
  const { label } = config.summary
  const lockStyle =
    geometry && typeof geometry.lockStyle === 'function'
      ? geometry.lockStyle
      : () => undefined
  const lockEdges =
    geometry && typeof geometry.lockEdges === 'function'
      ? geometry.lockEdges
      : () => NO_EDGES
  // a locked total carries its lock and, on the innermost lock, the edge that casts the shadow strip
  const placed = column => {
    const style = lockStyle(column.key)
    const edges = style ? lockEdges(column.key) : NO_EDGES
    return {
      style,
      'data-lock': style ? column.lock || undefined : undefined,
      'data-column-key': column.key,
      className: [
        cellClass(column),
        style && 'is-locked',
        edges.start && 'is-lock-start-edge',
        edges.end && 'is-lock-end-edge',
      ]
        .filter(Boolean)
        .join(' '),
    }
  }
  const leading = (layout.selectable ? 1 : 0) + (layout.expandable ? 1 : 0)
  // the label takes the first column without a total; failing that, the leading control cells
  const labelAt = layout.columns.findIndex(
    column => column.type !== 'actions' && !has(totals, column.key)
  )
  const labelLeads = labelAt < 0 && leading > 0
  const hiddenLabel = labelAt < 0 && !labelLeads

  const cells = layout.columns.map((column, index) => {
    const place = placed(column)
    if (index === labelAt)
      return (
        <th key={column.key} scope="row" {...place}>
          {label}
        </th>
      )
    return (
      <td key={column.key} {...place}>
        {hiddenLabel && index === 0 && (
          <span className="sr-only">{`${label} `}</span>
        )}
        {has(totals, column.key) ? (
          <SummaryValue
            text={formatTotal(column, totals[column.key])}
            clip={compact}
          />
        ) : null}
      </td>
    )
  })

  return (
    <tfoot>
      <tr className="me-table-summary">
        {labelLeads ? (
          <th scope="row" colSpan={leading} className="me-table-control">
            {label}
          </th>
        ) : (
          Array.from({ length: leading }, (_, i) => (
            <td key={`control-${i}`} className="me-table-control" />
          ))
        )}
        {cells}
        {layout.hasActions && <td className="me-table-actions" />}
      </tr>
    </tfoot>
  )
}
